import { useMemo, useState, type ReactElement } from 'react'
import { useQueryState } from 'nuqs'
import { parseAsZodSchema, parseAsSearchQuery } from '@util'
import {
  isEmpty,
  ProgramUserSortBySchema,
  ProgramUserStatusSchema,
  toUserId,
  type ProgramUserData,
  type ProgramUserSortBy,
  type ProgramUserSortOrder,
  type ProgramUserStatus,
} from '@terros/common'
import { type SortingState } from '@tanstack/react-table'
import { createFileRoute } from '@tanstack/react-router'
import { modals } from '@mantine/modals'
import { Tabs, TextInput } from '@mantine/core'
import { toReadableProgramUserStatus, useInfiniteProgramUsers, useTranslation } from '@front/model'
import { ButtonEdit, Page, useTanStackSortingState } from '@components'
import { ProgramHeading } from '../program_.$programId/ProgramHeader'
import { useProgramContext } from '../program_.$programId/context'
import { ProgramPageContainer } from '../program/ProgramPageContainer'
import { ProgramUserSection } from './ProgramUserSection'
import { ProgramUserForm } from './ProgramUserForm'
import { EnrollUsersButton } from './EnrollUsersButton'
import { DownloadProgramUsersCsv } from './DownloadProgramUsersCsv'

function toApiSort(sorting: SortingState): { sortBy?: ProgramUserSortBy; sortOrder?: ProgramUserSortOrder } {
  if (isEmpty(sorting)) return {}
  const { id, desc } = sorting[0]
  const parsed = ProgramUserSortBySchema.safeParse(id)
  if (!parsed.success) return {}
  return { sortBy: parsed.data, sortOrder: desc ? 'desc' : 'asc' }
}

function ProgramUsers(): ReactElement {
  const { t } = useTranslation()
  const { program, refetch: refetchProgram, isFetching: isFetchingProgram } = useProgramContext()

  const [activeTab, setActiveTab] = useQueryState<ProgramUserStatus>(
    'status',
    parseAsZodSchema(ProgramUserStatusSchema).withDefault('active')
  )
  const [query, setQuery] = useQueryState('query', parseAsSearchQuery.withDefault(''))

  const { sorting, setSorting } = useTanStackSortingState()
  const sort = useMemo(() => toApiSort(sorting), [sorting])

  const activeQuery = useInfiniteProgramUsers(program.programId, { status: 'active', query, ...sort })
  const completedQuery = useInfiniteProgramUsers(program.programId, { status: 'completed', query, ...sort })
  const pendingQuery = useInfiniteProgramUsers(program.programId, { status: 'pending', query, ...sort })
  const droppedQuery = useInfiniteProgramUsers(program.programId, { status: 'dropped', query, ...sort })

  const currentQuery = useMemo(() => {
    switch (activeTab) {
      case 'active':
        return activeQuery
      case 'completed':
        return completedQuery
      case 'pending':
        return pendingQuery
      case 'dropped':
        return droppedQuery
    }
  }, [activeTab, activeQuery, completedQuery, pendingQuery, droppedQuery])

  const [selectedUsers, setSelectedUsers] = useState<ProgramUserData[]>([])

  const refetch = (): void => {
    refetchProgram()
    activeQuery.refetch()
    completedQuery.refetch()
    pendingQuery.refetch()
    droppedQuery.refetch()
  }

  const allUsers = useMemo(
    () => [
      ...(activeQuery.data?.pages.flatMap((page) => page.users) ?? []),
      ...(completedQuery.data?.pages.flatMap((page) => page.users) ?? []),
      ...(pendingQuery.data?.pages.flatMap((page) => page.users) ?? []),
      ...(droppedQuery.data?.pages.flatMap((page) => page.users) ?? []),
    ],
    [activeQuery.data, completedQuery.data, pendingQuery.data, droppedQuery.data]
  )

  const unavailableUserIds = useMemo(() => allUsers.map(toUserId), [allUsers])

  const activeCount = activeQuery.data?.pages[0]?.total ?? 0
  const completedCount = completedQuery.data?.pages[0]?.total ?? 0
  const pendingCount = pendingQuery.data?.pages[0]?.total ?? 0
  const droppedCount = droppedQuery.data?.pages[0]?.total ?? 0

  const isFetching = currentQuery.isFetching

  const handleEdit = (): void => {
    if (isEmpty(selectedUsers)) return
    modals.open({
      id: 'program-edit-users',
      title: t('Edit {{count}} users', { count: selectedUsers.length }),
      children: (
        <ProgramUserForm
          users={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          close={() => modals.closeAll()}
          programId={program.programId}
        />
      ),
    })
  }

  const handleTabChange = (value: string | null): void => {
    if (!value) return
    setActiveTab(value as ProgramUserStatus)
    setSelectedUsers([])
  }

  return (
    <ProgramPageContainer>
      <ProgramHeading isFetching={isFetchingProgram || isFetching} refetch={refetch} title='Users'>
        <DownloadProgramUsersCsv programId={program.programId} query={query} status={activeTab} />
        <ButtonEdit disabled={isEmpty(selectedUsers)} onClick={handleEdit} variant='secondary'>
          {t('Edit {{count}} users', { count: selectedUsers.length })}
        </ButtonEdit>
        <EnrollUsersButton unavailableUserIds={unavailableUserIds} />
      </ProgramHeading>
      <Page.FunctionBar>
        <TextInput placeholder='Search...' value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
      </Page.FunctionBar>
      <Page.Content scrollArea>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value='active'>
              {toReadableProgramUserStatus('active')} ({activeCount})
            </Tabs.Tab>
            <Tabs.Tab value='completed'>
              {toReadableProgramUserStatus('completed')} ({completedCount})
            </Tabs.Tab>
            <Tabs.Tab value='pending'>
              {toReadableProgramUserStatus('pending')} ({pendingCount})
            </Tabs.Tab>
            <Tabs.Tab value='dropped'>
              {toReadableProgramUserStatus('dropped')} ({droppedCount})
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value={activeTab} pt='md'>
            <ProgramUserSection
              status={activeTab}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              query={currentQuery}
              sorting={sorting}
              setSorting={setSorting}
            />
          </Tabs.Panel>
        </Tabs>
      </Page.Content>
    </ProgramPageContainer>
  )
}

export const Route = createFileRoute('/program_/$programId/users/')({
  component: ProgramUsers,
})
